import React, {
  createContext, useContext, useState, useEffect,
} from 'react';
import { useQuery, useSubscription } from '@apollo/client';
import { useToast } from 'react-native-toast-notifications';
import useLocationTracking from '../hooks/useLocationTracking';
import { FindManyTrips } from '../gql/trip/query';
import { TRIP_AUTO_COMPLETED_SUBSCRIPTION } from '../gql/trip/subscription';
import { AuthContext } from './AuthContext';

export const LocationTrackingContext = createContext({
  location: null,
  errorMsg: null,
  isPermissionGranted: false,
  isBackgroundTracking: false,
  isDisclosureVisible: false,
  acceptDisclosure: () => {},
  declineDisclosure: () => {},
  requestPermissions: () => Promise.resolve(false),
  activeTrip: null,
  autoCompleted: false,
});

export function LocationTrackingProvider({ children }) {
  const authContext = useContext(AuthContext);
  const toast = useToast();
  const [activeTrackingTripId, setActiveTrackingTripId] = useState(null);
  const [autoCompleted, setAutoCompleted] = useState(false);

  // Only query trips if user is authenticated
  const { data, refetch } = useQuery(FindManyTrips, {
    skip: !authContext.authState.authenticated,
    pollInterval: 60000, // Poll every 30 seconds to check for new IN_PROGRESS trips
  });

  // Find the active IN_PROGRESS trip for location tracking
  const inProgressTrip = data?.myTripsAsDriver?.find((t) => t.status === 'IN_PROGRESS');

  // Automatically track location for IN_PROGRESS trips
  useEffect(() => {
    if (inProgressTrip) {
      setActiveTrackingTripId(inProgressTrip.id);
      setAutoCompleted(false);
    } else {
      setActiveTrackingTripId(null);
    }
  }, [inProgressTrip]);

  // Use the location tracking hook at the provider level
  const {
    location,
    errorMsg,
    isPermissionGranted,
    isBackgroundTracking,
    isDisclosureVisible,
    acceptDisclosure,
    declineDisclosure,
    requestPermissions,
    stopTracking,
  } = useLocationTracking(activeTrackingTripId, !!activeTrackingTripId);

  // Subscribe to auto-complete events for the active trip
  useSubscription(TRIP_AUTO_COMPLETED_SUBSCRIPTION, {
    variables: { tripId: activeTrackingTripId },
    skip: !activeTrackingTripId,
    onData: () => {
      stopTracking();
      setActiveTrackingTripId(null);
      setAutoCompleted(true);
      toast.show('Vous êtes arrivé à destination. Le trajet est terminé.', { type: 'success' });
      refetch();
    },
  });

  const contextValue = {
    location,
    errorMsg,
    isPermissionGranted,
    isBackgroundTracking,
    isDisclosureVisible,
    acceptDisclosure,
    declineDisclosure,
    requestPermissions,
    activeTrip: inProgressTrip,
    autoCompleted,
  };

  return (
    <LocationTrackingContext.Provider value={contextValue}>
      {children}
    </LocationTrackingContext.Provider>
  );
}

// Custom hook to use the location tracking context
export function useLocationTrackingContext() {
  const context = useContext(LocationTrackingContext);
  if (!context) {
    throw new Error('useLocationTrackingContext must be used within LocationTrackingProvider');
  }
  return context;
}
